import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { placements } from "../api/placements";
import type { PlacementCreateCommand } from "../api/types";
import { ApiError } from "../api/client";
import { PlacementForm } from "./PlacementForm";

export function PlacementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const [savedRevision, setSavedRevision] = useState(0);
  const detail = useQuery({ queryKey: ["placement", id], queryFn: () => placements.get(id!), enabled: Boolean(id) });
  const revise = useMutation({
    mutationFn: ({ command: { placementKey: _immutableKey, ...command }, baseEntityVersion }: { command: PlacementCreateCommand; baseEntityVersion: number }) =>
      placements.revise(id!, { ...command, entityVersion: baseEntityVersion }),
    onSuccess: async () => {
      setMessage("Новая ревизия сохранена.");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["placement", id] }),
        queryClient.invalidateQueries({ queryKey: ["placements"] }),
      ]);
      // Reset the editor only after this tab successfully saves. A background
      // query refresh must not discard an administrator's in-progress draft.
      setSavedRevision((value) => value + 1);
    },
    onError: (failure) => setMessage(failure instanceof ApiError && failure.status === 409 ? "Плейсмент изменился. Обновите страницу и повторите." : "Не удалось сохранить ревизию."),
  });
  const archive = useMutation({
    mutationFn: () => placements.archive(id!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["placements"] });
      navigate("/placements");
    },
    onError: () => setMessage("Не удалось архивировать плейсмент."),
  });

  if (!id) return <p className="error">Не указан идентификатор.</p>;
  if (detail.isLoading) return <p className="muted">Загружаю…</p>;
  if (detail.isError) return <p className="error">Не удалось загрузить плейсмент. <button type="button" onClick={() => void detail.refetch()}>Повторить</button></p>;
  if (!detail.data) return null;

  return (
    <>
      <p className="muted"><Link to="/placements">← Плейсменты</Link></p>
      <div className="page-heading">
        <div><h2>{detail.data.displayName}</h2><p className="muted"><code>{detail.data.placementKey}</code> · {detail.data.status}</p></div>
        {detail.data.status === "active" && <button className="danger" type="button" disabled={archive.isPending} onClick={() => { if (window.confirm("Архивировать плейсмент? Новые выдачи его больше не используют.")) archive.mutate(); }}>{archive.isPending ? "Архивирую…" : "Архивировать"}</button>}
      </div>
      {detail.data.status === "active" ? (
        <PlacementForm
          key={`${detail.data.id}:${savedRevision}`}
          placement={detail.data}
          busy={revise.isPending}
          submitLabel="Сохранить новую ревизию"
          onSubmit={(command, baseEntityVersion) => {
            setMessage(null);
            revise.mutate({ command, baseEntityVersion: baseEntityVersion! });
          }}
        />
      ) : <div className="panel"><p className="muted">Архивный плейсмент доступен только для чтения.</p></div>}
      {message && <p className={message.startsWith("Новая") ? "muted" : "error"} role="status">{message}</p>}
    </>
  );
}
